/**
 * SitePick front — お気に入り / 比較トグルの同期（docs/06 §2 / §12 / §13 / §15）
 *
 * 既存 Phase 3 markup（`.js-sp-favorite` / `.js-sp-compare` / `data-sp-id` / `aria-pressed`）を利用。
 * クリックでストアを更新し、ページ内の全ボタン・ヘッダーバッジの `aria-pressed` / `is-active` を同期。
 * ストアが `storage` イベントを取り込むため、別タブの変更も同じ経路で反映される。
 */

import { favoritesStore } from "./favorites-store.js";
import { compareStore } from "./compare-store.js";
import { announce } from "./live-region.js";
import {
  readItem,
  addToWishlist,
  removeFromWishlist,
  addToCompare,
  removeFromCompare,
} from "./analytics.js";

const CONFIG = {
  favorite: {
    store: favoritesStore,
    selector: ".js-sp-favorite",
    countKey: "favorites",
  },
  compare: {
    store: compareStore,
    selector: ".js-sp-compare",
    countKey: "compare",
  },
};

/**
 * ローカライズ済みメッセージ（無ければ日本語の既定）。
 *
 * @param {string} key
 * @param {string} fallback
 * @returns {string}
 */
function msg(key, fallback) {
  const m = (window.SITEPICK && window.SITEPICK.messages) || {};
  return typeof m[key] === "string" && m[key] ? m[key] : fallback;
}

/**
 * 1 種類（favorite / compare）のボタン群を現在のストア状態に合わせる。
 *
 * @param {string} kind
 */
function syncKind(kind) {
  const { store, selector, countKey } = CONFIG[kind];
  const ids = store.get();
  const full = store.isFull();

  document.querySelectorAll(selector).forEach((btn) => {
    const id = parseInt(btn.getAttribute("data-sp-id") || "0", 10);
    if (!Number.isInteger(id) || id <= 0) {
      return;
    }
    const active = ids.indexOf(id) !== -1;
    btn.setAttribute("aria-pressed", active ? "true" : "false");
    btn.classList.toggle("is-active", active);
    // 比較が満杯で未選択のボタンは「追加不可」を示す（無効化はしない＝理由を通知できる）。
    if (kind === "compare") {
      btn.classList.toggle("is-disabled", full && !active);
    }
  });

  document.querySelectorAll(`[data-sp-count="${countKey}"]`).forEach((el) => {
    const n = ids.length;
    el.textContent = String(n);
    el.classList.toggle("is-empty", n === 0);
    const wrap = el.closest("[data-sp-count-wrap]") || el;
    if (wrap instanceof HTMLElement) {
      wrap.hidden = n === 0;
    }
  });
}

/**
 * すべて同期。
 */
function syncAll() {
  syncKind("favorite");
  syncKind("compare");
}

/**
 * favorite ボタンのクリック処理。
 *
 * @param {HTMLElement} btn
 * @param {number} id
 */
function handleFavorite(btn, id) {
  const result = favoritesStore.toggle(id);
  if (!result.ok) {
    if (result.reason === "limit") {
      announce(msg("favoriteLimit", "お気に入りに追加できる上限に達しています。"));
    }
    return;
  }
  const item = readItem(btn);
  if (result.active) {
    announce(msg("favoriteAdded", "お気に入りに追加しました"));
    addToWishlist(item);
  } else {
    announce(msg("favoriteRemoved", "お気に入りから削除しました"));
    removeFromWishlist(item);
  }
}

/**
 * compare ボタンのクリック処理。
 *
 * @param {HTMLElement} btn
 * @param {number} id
 */
function handleCompare(btn, id) {
  const wasActive = compareStore.has(id);
  const result = compareStore.toggle(id);
  if (!result.ok) {
    if (result.reason === "limit") {
      const text = msg(
        "compareLimit",
        `比較できるのは最大 ${compareStore.max()} 件です。`
      ).replace("%d", String(compareStore.max()));
      // 読み上げ（従来）に加えて、押した本人に見える形でも理由を出す。
      // ボタンが薄いだけだと「なぜ押せないのか」が分からない。
      showButtonHint(btn, text);
      announce(text);
    }
    return;
  }
  if (result.active && !wasActive) {
    announce(msg("compareAdded", "比較に追加しました"));
    addToCompare(id, compareStore.count());
  } else if (!result.active) {
    announce(msg("compareRemoved", "比較から削除しました"));
    removeFromCompare(id, compareStore.count());
  }
}

/**
 * 初期化（多重登録しない）。
 *
 * @param {Document|HTMLElement} [root=document]
 * @returns {void}
 */
export function initToggleButtons(root = document) {
  if (document.documentElement.dataset.spToggleReady === "1") {
    syncAll();
    return;
  }
  document.documentElement.dataset.spToggleReady = "1";

  document.addEventListener("click", (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) {
      return;
    }
    const btn = target.closest(".js-sp-favorite, .js-sp-compare");
    if (!(btn instanceof HTMLElement)) {
      return;
    }
    const id = parseInt(btn.getAttribute("data-sp-id") || "0", 10);
    if (!Number.isInteger(id) || id <= 0) {
      return;
    }
    event.preventDefault();
    if (btn.classList.contains("js-sp-favorite")) {
      handleFavorite(btn, id);
    } else {
      handleCompare(btn, id);
    }
  });

  favoritesStore.subscribe(() => syncKind("favorite"));
  compareStore.subscribe(() => syncKind("compare"));

  // ファセット等で一覧が差し替わったら、新しいカードのトグル状態を同期する。
  document.addEventListener("sitepick:content:updated", syncAll);

  // キャッシュされた HTML でも初期状態を localStorage から反映（docs/06 §2）。
  syncAll();

  // root 引数は将来の部分初期化用（現状は document 全体で委譲）。
  void root;
}

/**
 * ボタンの近くに理由を出して、少し経ったら消す。
 *
 * カードの高さを変えないよう absolute で重ねる。同じボタンを連打しても
 * 重ならないように、既に出ているものは作り直す。
 *
 * @param {HTMLElement} btn
 * @param {string} text
 */
function showButtonHint(btn, text) {
  const host = btn.parentElement;
  if (!host) return;

  const prev = host.querySelector(".c-toggle-hint");
  if (prev) prev.remove();

  const hint = document.createElement("span");
  hint.className = "c-toggle-hint";
  hint.setAttribute("role", "status");
  hint.textContent = text;
  // 親に position が無いとページ全体を基準に飛ぶ。
  if (getComputedStyle(host).position === "static") host.style.position = "relative";
  host.appendChild(hint);

  // ボタン列の右の空きに置く。host はカード幅いっぱいの flex 行なので、
  // CSS だけで `left: 100%` にすると**カードの外**へ出て隣のカードに重なる。
  // 押したボタンの右端を基準に、カード内へ収まる範囲で置く。
  const gap = 10;
  const startX = btn.offsetLeft + btn.offsetWidth + gap;
  const room = host.clientWidth - startX;
  if (room >= 140) {
    hint.style.left = `${startX}px`;
    hint.style.maxWidth = `${room}px`;
  } else {
    // 横に置けない狭さなら、ボタン列の真下へ（CTA は隠さない）。
    hint.style.left = "0";
    hint.style.right = "auto";
    hint.style.bottom = "auto";
    hint.style.top = "calc(100% + 6px)";
    hint.style.maxWidth = "100%";
  }

  // 追加直後にクラスを付けてトランジションさせる（2 フレーム待つ）。
  requestAnimationFrame(() => requestAnimationFrame(() => hint.classList.add("is-visible")));

  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  window.setTimeout(() => {
    hint.classList.remove("is-visible");
    if (reduce) {
      hint.remove();
      return;
    }
    hint.addEventListener("transitionend", () => hint.remove(), { once: true });
    // transitionend が来ない環境でも必ず消す。
    window.setTimeout(() => hint.remove(), 600);
  }, 2200);
}
